import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import {
  withdraw,
  getTransactions,
  getTransactionStatistics,
} from "../redux/slices/transactionSlice";
import { getWallet } from "../redux/slices/walletSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  ArrowUpRight,
  Wallet as WalletIcon,
  AlertCircle,
  CheckCircle,
  History,
  RefreshCw,
  TrendingUp,
  Calculator,
  AlertTriangle,
  Key,
  Shield,
} from "lucide-react";
import toast from "react-hot-toast";
import { formatFiat, formatCrypto } from "../utils/currency";
import { transactionService } from "../services/api";
import { Transaction } from "../types";
import { Link } from "react-router-dom";

interface CryptoPrice {
  [key: string]: {
    price: number;
    network: string;
    crypto_id: string;
  };
}

// Default currencies with initial values (same as Deposit.tsx)
const INITIAL_CURRENCIES = {
  BTC: { price: 0, network: "Bitcoin", crypto_id: "bitcoin" },
  ETH: { price: 0, network: "Ethereum", crypto_id: "ethereum" },
  USDT: { price: 0, network: "Ethereum", crypto_id: "tether" },
  BNB: { price: 0, network: "Binance Smart Chain", crypto_id: "binancecoin" },
  SOL: { price: 0, network: "Solana", crypto_id: "solana" },
};

interface CalculatedWithdrawal {
  usd_amount: number;
  crypto_amount: number;
  currency: string;
  crypto_price: number;
  network: string;
  meets_minimum: boolean;
  minimum_required: number;
}

const Withdrawal: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { wallet, isLoading: walletLoading } = useSelector(
    (state: RootState) => state.wallet
  );
  const { transactions, statistics } = useSelector(
    (state: RootState) => state.transactions
  );

  const [loading, setLoading] = useState(false);
  const [fetchingPrices, setFetchingPrices] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [formData, setFormData] = useState({
    usd_amount: "",
    currency: "BTC",
  });
  const [cryptoPrices, setCryptoPrices] = useState<CryptoPrice>(() => {
    // Try to load saved prices from localStorage on initial render (SAME AS DEPOSIT.TSX)
    const savedPrices = localStorage.getItem("obelisk_crypto_prices");
    if (savedPrices) {
      try {
        const parsed = JSON.parse(savedPrices);
        // Merge with initial currencies to ensure all required fields exist
        return { ...INITIAL_CURRENCIES, ...parsed };
      } catch (e) {
        return INITIAL_CURRENCIES;
      }
    }
    return INITIAL_CURRENCIES;
  });
  const [calculatedWithdrawal, setCalculatedWithdrawal] =
    useState<CalculatedWithdrawal | null>(null);
  const [lastUpdate, setLastUpdate] = useState<string>(() => {
    // Try to load last update time from localStorage
    const savedUpdate = localStorage.getItem("obelisk_prices_last_update");
    return savedUpdate || "";
  });

  useEffect(() => {
    fetchCryptoPrices();
    dispatch(getTransactions({ type: "withdrawal" }) as any);
    dispatch(getTransactionStatistics() as any);

    // Only fetch wallet if user has wallet_connected flag
    if (user?.wallet_connected && !wallet) {
      dispatch(getWallet() as any);
    }

    // Refresh prices every 60 seconds (SAME AS DEPOSIT.TSX)
    const interval = setInterval(fetchCryptoPrices, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [dispatch, user, wallet]);

  // EXACTLY the same function as in Deposit.tsx
  const savePricesToStorage = (prices: CryptoPrice, timestamp: number) => {
    try {
      // Save prices to localStorage
      localStorage.setItem("obelisk_crypto_prices", JSON.stringify(prices));

      // Save last update timestamp
      localStorage.setItem(
        "obelisk_prices_last_update_timestamp",
        timestamp.toString()
      );

      // Save formatted last update time for display
      const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
      setLastUpdate(updateTime);
      localStorage.setItem("obelisk_prices_last_update", updateTime);
    } catch (error) {
      console.error("Error saving prices to storage:", error);
    }
  };

  // EXACTLY the same function as in Deposit.tsx
  const fetchCryptoPrices = async () => {
    setFetchingPrices(true);
    try {
      const response = await transactionService.getCryptoPrices();

      if (response.success && response.prices) {
        // Merge new prices with existing ones (so we don't lose old values)
        const mergedPrices = { ...cryptoPrices };
        let hasNewData = false;

        Object.entries(response.prices).forEach(([currency, data]) => {
          // Type-safe check for price data
          const priceData = data as any;
          if (
            priceData &&
            typeof priceData === "object" &&
            typeof priceData.price === "number" &&
            priceData.price > 0
          ) {
            mergedPrices[currency] = priceData;
            hasNewData = true;
          }
        });

        // Only update and save if we got new valid data
        if (hasNewData) {
          setCryptoPrices(mergedPrices);
          savePricesToStorage(
            mergedPrices,
            response.timestamp || Math.floor(Date.now() / 1000)
          );

          // Show success toast only if we actually got new data
          if (Object.keys(response.prices).length > 0) {
            toast.success("Prices updated successfully");
          }
        } else {
          // No new valid data, keep existing prices

        }
      } else {
        // API call succeeded but returned no data or error
        toast.success("Using cached prices - API returned no data");

        // Load saved timestamp if available
        const savedTimestamp = localStorage.getItem(
          "obelisk_prices_last_update_timestamp"
        );
        if (savedTimestamp) {
          const timestamp = parseInt(savedTimestamp);
          const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
          setLastUpdate(updateTime);
        }
      }
    } catch (error: any) {
      console.error("Price fetch error:", error);

      // On network error, use cached prices
      const savedTimestamp = localStorage.getItem(
        "obelisk_prices_last_update_timestamp"
      );
      if (savedTimestamp) {
        const timestamp = parseInt(savedTimestamp);
        const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
        setLastUpdate(updateTime);
        toast.error("Network error - Using cached prices");
      } else {
        toast.error("Failed to fetch prices - Please try again");
      }
    } finally {
      setFetchingPrices(false);
    }
  };

  const handleCalculate = async () => {
    if (!formData.usd_amount || parseFloat(formData.usd_amount) <= 0) {
      toast.error("Please enter a valid USD amount");
      return;
    }

    // Get price from localStorage cache (SAME AS DEPOSIT.TSX)
    const currentPrice = cryptoPrices[formData.currency]?.price || 0;
    if (currentPrice === 0) {
      toast.error("Please wait for prices to load");
      return;
    }

    const usdAmount = parseFloat(formData.usd_amount);
    const cryptoAmount = usdAmount / currentPrice;
    const minWithdrawalUsd = 20;
    const meetsMinimum = usdAmount >= minWithdrawalUsd;

    // Check if user has enough balance
    if (user && usdAmount > user.balance) {
      toast.error("Insufficient balance");
      return;
    }

    // Get network info from cached data
    const network =
      cryptoPrices[formData.currency]?.network ||
      INITIAL_CURRENCIES[formData.currency as keyof typeof INITIAL_CURRENCIES]
        ?.network ||
      "Unknown Network";

    // Create local calculation
    const localCalculation: CalculatedWithdrawal = {
      usd_amount: usdAmount,
      crypto_amount: cryptoAmount,
      currency: formData.currency,
      crypto_price: currentPrice,
      network: network,
      meets_minimum: meetsMinimum,
      minimum_required: minWithdrawalUsd,
    };

    if (!meetsMinimum) {
      toast.error(
        `Minimum withdrawal is ${formatFiat(
          minWithdrawalUsd
        )}. Your amount is ${formatFiat(usdAmount)}.`
      );
      setCalculatedWithdrawal(localCalculation);
      return;
    }

    setCalculating(true);
    try {
      // Optionally validate with backend (similar to deposit)
      setCalculatedWithdrawal(localCalculation);
    } catch (error) {
      console.error("Calculation error:", error);
    } finally {
      setCalculating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.usd_amount || parseFloat(formData.usd_amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (!calculatedWithdrawal) {
      toast.error("Please calculate the amount first");
      return;
    }

    if (!calculatedWithdrawal.meets_minimum) {
      toast.error(
        `Minimum withdrawal is ${formatFiat(
          calculatedWithdrawal.minimum_required
        )}`
      );
      return;
    }

    setLoading(true);
    try {
      await dispatch(
        withdraw({
          usd_amount: parseFloat(formData.usd_amount),
          currency: formData.currency,
          current_price: calculatedWithdrawal.crypto_price,
        }) as any
      );

      toast.success("Withdrawal submitted successfully!");

      // Reset form
      setFormData({
        usd_amount: "",
        currency: "BTC",
      });
      setCalculatedWithdrawal(null);

      // Refresh transactions and statistics
      dispatch(getTransactions({ type: "withdrawal" }) as any);
      dispatch(getTransactionStatistics() as any);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to submit withdrawal");
    } finally {
      setLoading(false);
    }
  };

  const handleMaxAmount = () => {
    if (user) {
      setFormData({ ...formData, usd_amount: user.balance.toString() });
    }
  };

  const recentWithdrawals = transactions
    .filter((t) => t.type === "withdrawal")
    .slice(0, 3);

  const getCurrentPrice = (currency: string) => {
    return cryptoPrices[currency]?.price || 0;
  };

  // Get last update time for display (same as Deposit.tsx)
  const getLastUpdateDisplay = () => {
    if (fetchingPrices) return "Updating...";
    if (lastUpdate) return `Last updated: ${lastUpdate}`;

    // Try to get from localStorage if state is empty
    const savedUpdate = localStorage.getItem("obelisk_prices_last_update");
    if (savedUpdate) {
      setLastUpdate(savedUpdate);
      return `Last updated: ${savedUpdate}`;
    }

    return "Never updated";
  };

  // Format price display - shows dash for zero prices, otherwise shows saved price
  const formatPriceDisplay = (price: number, currency: string) => {
    if (price === 0) {
      return fetchingPrices ? "Loading..." : "—";
    }

    // Format based on currency value
    if (price > 1000) {
      return `$${price.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    } else if (price > 1) {
      return `$${price.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      })}`;
    } else {
      return `$${price.toLocaleString(undefined, {
        minimumFractionDigits: 6,
        maximumFractionDigits: 6,
      })}`;
    }
  };

  // Helper function to safely extract transaction details
  const getTransactionDetails = (transaction: Transaction) => {
    if (!transaction.admin_notes) {
      return null;
    }

    try {
      if (typeof transaction.admin_notes === "string") {
        const parsed = JSON.parse(transaction.admin_notes);
        return {
          crypto_amount: parsed.crypto_amount,
          currency: parsed.currency,
          usd_amount: parsed.usd_amount,
          network: parsed.network,
        };
      } else if (typeof transaction.admin_notes === "object") {
        return {
          crypto_amount: (transaction.admin_notes as any).crypto_amount,
          currency: (transaction.admin_notes as any).currency,
          usd_amount: (transaction.admin_notes as any).usd_amount,
          network: (transaction.admin_notes as any).network,
        };
      }
    } catch (error) {
      console.error("Error parsing admin notes:", error);
    }

    return null;
  };

  // If wallet is not connected, show connect wallet prompt
  if (!wallet && !walletLoading && (!user || !user.wallet_connected)) {
    return (
      <div className={GlobalStyles.pageContainer}>
        <div className={GlobalStyles.contentContainer}>
          {/* Header */}
          <div className={GlobalStyles.sectionSpacing}>
            <h1 className={GlobalStyles.heading1}>Withdraw Funds</h1>
            <p className={GlobalStyles.bodyText + " mt-2"}>
              Withdraw cryptocurrency to wallet
            </p>
          </div>

          {/* Wallet Not Connected Card */}
          <div className={GlobalStyles.card}>
            <div className="text-center">
              <div className="mx-auto w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                <WalletIcon className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className={GlobalStyles.heading2 + " mb-2"}>
                Wallet Not Connected
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                You need to connect your wallet before you can make withdrawals
              </p>

              {/* Security Warning */}
              <div
                className={`${GlobalStyles.alert} ${GlobalStyles.alertWarning} mb-6 max-w-md mx-auto`}
              >
                <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">Withdrawal Security</p>
                  <p className="text-sm mt-1">
                    Withdrawals can only be sent to your connected wallet
                    address
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/wallet"
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium}`}
                >
                  <WalletIcon className="h-5 w-5 mr-2" />
                  Connect Wallet
                </Link>
                <Link
                  to="/deposit"
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium}`}
                >
                  <ArrowUpRight className="h-5 w-5 mr-2 rotate-180" />
                  Make Deposit First
                </Link>
              </div>

              {/* Wallet Benefits */}
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h4 className={GlobalStyles.heading3 + " mb-4"}>
                  Why Connect a Wallet?
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Shield className="h-6 w-6 text-primary" />
                    </div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Secure Withdrawals
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Withdrawals only to your verified wallet
                    </p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
                      <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                    </div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Quick Processing
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Faster withdrawal processing with connected wallet
                    </p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Key className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Full Control
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      You control your private keys and funds
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while checking wallet status
  if (walletLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className={`${GlobalStyles.spinner} h-8 w-8`}></div>
      </div>
    );
  }

  // Wallet is connected, show withdrawal form
  return (
    <div className={GlobalStyles.pageContainer}>
      <div className={GlobalStyles.contentContainer}>
        {/* Header */}
        <div className={GlobalStyles.sectionSpacing}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className={GlobalStyles.heading1}>Withdraw Funds</h1>
              <p className={GlobalStyles.bodyText + " mt-2"}>
                Withdraw cryptocurrency using cached prices (updates
                automatically)
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Connected Wallet Indicator */}
              <div className="flex items-center px-3 py-1.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <WalletIcon className="h-4 w-4 text-green-600 dark:text-green-400 mr-2" />
                <span className="text-sm font-medium text-green-700 dark:text-green-300">
                  Wallet Connected
                </span>
              </div>
              <button
                onClick={fetchCryptoPrices}
                disabled={fetchingPrices}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} disabled:opacity-50`}
              >
                {fetchingPrices ? (
                  <div className="flex items-center">
                    <div
                      className={`${GlobalStyles.spinner} w-4 h-4 mr-2`}
                    ></div>
                    Updating...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh Prices
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Withdrawal Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Price Ticker */}
            <div className={GlobalStyles.card}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={GlobalStyles.heading3}>Crypto Prices</h3>
                <div className="flex items-center gap-2 text-sm">
                  <TrendingUp
                    className={`h-4 w-4 ${
                      fetchingPrices ? "text-yellow-500" : "text-green-500"
                    }`}
                  />
                  <span
                    className={`${
                      fetchingPrices
                        ? "text-yellow-500"
                        : "text-gray-500 dark:text-gray-400"
                    }`}
                  >
                    {getLastUpdateDisplay()}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Object.keys(INITIAL_CURRENCIES).map((currency) => {
                  const priceData = cryptoPrices[currency];
                  const price = priceData?.price || 0;
                  const network =
                    priceData?.network ||
                    INITIAL_CURRENCIES[
                      currency as keyof typeof INITIAL_CURRENCIES
                    ]?.network ||
                    "Unknown";

                  return (
                    <div
                      key={currency}
                      className={`p-3 rounded-lg text-center transition-all duration-200 ${
                        fetchingPrices
                          ? "bg-gray-100 dark:bg-gray-800/30 animate-pulse"
                          : price === 0
                          ? "bg-gray-100 dark:bg-gray-800/30"
                          : "bg-gray-50 dark:bg-gray-800/50"
                      }`}
                    >
                      <div className="font-semibold text-gray-900 dark:text-white mb-1">
                        {currency}
                      </div>
                      <div
                        className={`text-sm font-medium ${
                          fetchingPrices
                            ? "text-gray-400 dark:text-gray-500"
                            : price === 0
                            ? "text-gray-500 dark:text-gray-400"
                            : "text-green-600 dark:text-green-400"
                        }`}
                      >
                        {formatPriceDisplay(price, currency)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {network}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Withdrawal Form */}
            <div className={GlobalStyles.card}>
              <div className="flex items-center justify-between mb-6">
                <h3 className={GlobalStyles.heading3}>Withdrawal Details</h3>
                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                  <WalletIcon className="h-4 w-4 mr-1" />
                  Balance: {formatFiat(user?.balance || 0)}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* USD Amount */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className={GlobalStyles.inputLabel}>
                      USD Amount
                    </label>
                    <button
                      type="button"
                      onClick={handleMaxAmount}
                      className="text-sm text-primary hover:text-primary/80"
                    >
                      Use Max
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      value={formData.usd_amount}
                      onChange={(e) =>
                        setFormData({ ...formData, usd_amount: e.target.value })
                      }
                      className={`${GlobalStyles.input} pr-24`}
                      placeholder="0.00"
                      required
                      min="0.01"
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <span className="text-gray-700 dark:text-gray-300 font-medium">
                        USD
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between mt-1">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Your balance: {formatFiat(user?.balance || 0)}
                    </p>
                    {user && formData.usd_amount && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Remaining:{" "}
                        {formatFiat(
                          user.balance - parseFloat(formData.usd_amount)
                        )}
                      </p>
                    )}
                  </div>
                </div>

                {/* Currency Selection */}
                <div>
                  <label className={GlobalStyles.inputLabel}>
                    Cryptocurrency
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) =>
                      setFormData({ ...formData, currency: e.target.value })
                    }
                    className={GlobalStyles.input}
                    disabled={fetchingPrices}
                  >
                    {Object.keys(INITIAL_CURRENCIES).map((currency) => (
                      <option key={currency} value={currency}>
                        {currency}
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Current price:{" "}
                    {formatPriceDisplay(
                      getCurrentPrice(formData.currency),
                      formData.currency
                    )}
                  </p>
                </div>

                {/* Connected Wallet Display (Read Only) */}
                {wallet && (
                  <div>
                    <label className={GlobalStyles.inputLabel}>
                      Destination Wallet
                    </label>
                    <div className="p-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {wallet.wallet_name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-md">
                            {wallet.address}
                          </p>
                        </div>
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Withdrawals will be sent to your connected wallet address
                    </p>
                  </div>
                )}

                {/* Calculate Button */}
                <div>
                  <button
                    type="button"
                    onClick={handleCalculate}
                    disabled={
                      calculating ||
                      !formData.usd_amount ||
                      fetchingPrices ||
                      getCurrentPrice(formData.currency) === 0
                    }
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} w-full disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {calculating ? (
                      <div className="flex items-center justify-center">
                        <div
                          className={`${GlobalStyles.spinner} w-4 h-4 mr-2`}
                        ></div>
                        Calculating...
                      </div>
                    ) : fetchingPrices ? (
                      <div className="flex items-center justify-center">
                        <div
                          className={`${GlobalStyles.spinner} w-4 h-4 mr-2`}
                        ></div>
                        Loading Prices...
                      </div>
                    ) : getCurrentPrice(formData.currency) === 0 ? (
                      <div className="flex items-center justify-center">
                        <div
                          className={`${GlobalStyles.spinner} w-4 h-4 mr-2`}
                        ></div>
                        Waiting for Price...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <Calculator className="h-4 w-4 mr-2" />
                        Calculate Amount
                      </div>
                    )}
                  </button>
                </div>

                {/* Calculated Withdrawal Display */}
                {calculatedWithdrawal && (
                  <div
                    className={`p-4 border rounded-lg ${
                      calculatedWithdrawal.meets_minimum
                        ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                        : "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4
                        className={`font-medium ${
                          calculatedWithdrawal.meets_minimum
                            ? "text-green-800 dark:text-green-300"
                            : "text-yellow-800 dark:text-yellow-300"
                        }`}
                      >
                        Withdrawal Summary
                      </h4>
                      {calculatedWithdrawal.meets_minimum ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-yellow-500" />
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          USD Amount:
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatFiat(calculatedWithdrawal.usd_amount)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Exchange Rate:
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          1 {calculatedWithdrawal.currency} = $
                          {calculatedWithdrawal.crypto_price.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Network:
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {calculatedWithdrawal.network}
                        </span>
                      </div>
                      <div className="pt-2 border-t border-current">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            You Will Receive:
                          </span>
                          <span
                            className={`text-lg font-bold ${
                              calculatedWithdrawal.meets_minimum
                                ? "text-green-600 dark:text-green-400"
                                : "text-yellow-600 dark:text-yellow-400"
                            }`}
                          >
                            {formatCrypto(
                              calculatedWithdrawal.crypto_amount,
                              calculatedWithdrawal.currency
                            )}
                          </span>
                        </div>
                      </div>
                      {!calculatedWithdrawal.meets_minimum && (
                        <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                          <p className="text-sm text-red-600 dark:text-red-400 text-center">
                            Minimum withdrawal:{" "}
                            {formatFiat(calculatedWithdrawal.minimum_required)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div>
                  <button
                    type="submit"
                    disabled={
                      loading ||
                      !calculatedWithdrawal ||
                      fetchingPrices ||
                      !calculatedWithdrawal?.meets_minimum
                    }
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonLarge} w-full disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div
                          className={`${GlobalStyles.spinner} w-5 h-5 mr-2`}
                        ></div>
                        Processing...
                      </div>
                    ) : fetchingPrices ? (
                      <div className="flex items-center justify-center">
                        <div
                          className={`${GlobalStyles.spinner} w-4 h-4 mr-2`}
                        ></div>
                        Loading...
                      </div>
                    ) : (
                      <>
                        <ArrowUpRight className="h-5 w-5 mr-2" />
                        Confirm Withdrawal
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Recent Withdrawals */}
            <div className={GlobalStyles.card}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={GlobalStyles.heading3}>Recent Withdrawals</h3>
                <button
                  onClick={() => (window.location.href = "/transactions")}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonSmall}`}
                >
                  <History className="h-4 w-4 mr-2" />
                  View All
                </button>
              </div>
              <div className="space-y-3">
                {recentWithdrawals.length > 0 ? (
                  recentWithdrawals.map((transaction) => {
                    const details = getTransactionDetails(transaction);

                    return (
                      <div
                        key={transaction.id}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {formatFiat(transaction.amount)}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {details &&
                            details.crypto_amount &&
                            details.currency
                              ? formatCrypto(
                                  details.crypto_amount,
                                  details.currency
                                )
                              : formatFiat(transaction.amount)}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            {new Date(
                              transaction.created_at
                            ).toLocaleDateString()}
                          </p>
                        </div>
                        <span
                          className={`${GlobalStyles.badge} ${
                            transaction.status === "confirmed"
                              ? GlobalStyles.badgeSuccess
                              : transaction.status === "pending"
                              ? GlobalStyles.badgeWarning
                              : GlobalStyles.badgeError
                          }`}
                        >
                          {transaction.status}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500 dark:text-gray-400">
                      No withdrawals yet
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Info & Stats */}
          <div className="space-y-6">
            {/* Connected Wallet Info */}
            {wallet && (
              <div className={GlobalStyles.card}>
                <h3 className={`${GlobalStyles.heading3} mb-4`}>Your Wallet</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {wallet.wallet_name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]">
                        {wallet.address}
                      </p>
                    </div>
                    <span
                      className={`${GlobalStyles.badge} ${GlobalStyles.badgeSuccess}`}
                    >
                      Active
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-center">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Type
                      </p>
                      <p className="font-medium text-gray-900 dark:text-white mt-1">
                        Multi-chain
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-center">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Status
                      </p>
                      <p className="font-medium text-green-600 dark:text-green-400 mt-1">
                        Verified
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/wallet"
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full`}
                  >
                    <WalletIcon className="h-4 w-4 mr-2" />
                    Manage Wallet
                  </Link>
                </div>
              </div>
            )}

            {/* Important Info */}
            <div className={GlobalStyles.card}>
              <h3 className={`${GlobalStyles.heading3} mb-4`}>
                Important Information
              </h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Minimum withdrawal: $20 USD
                  </p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Withdrawals use live prices
                  </p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    All withdrawals require confirmation
                  </p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Funds will be sent to your connected wallet
                  </p>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className={GlobalStyles.card}>
              <h3 className={`${GlobalStyles.heading3} mb-4`}>
                Transaction Stats
              </h3>
              <div className="space-y-4">
                {statistics ? (
                  <>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Total Withdrawals
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formatFiat(statistics.total_withdrawals)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Pending Withdrawals
                      </p>
                      <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                        {formatFiat(statistics.pending_withdrawals)}
                      </p>
                    </div>
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Total Withdrawal Count
                      </p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">
                        {
                          transactions.filter((t) => t.type === "withdrawal")
                            .length
                        }
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500 dark:text-gray-400">
                      Loading statistics...
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Warning */}
            <div
              className={`${GlobalStyles.alert} ${GlobalStyles.alertWarning}`}
            >
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <p className="text-sm">
                Withdrawals are processed manually by admin. Please allow 1-2
                hours for processing.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Withdrawal;
